import React from 'react';

import { makeStyles, Container, Grid, Button } from '@material-ui/core';
import * as microsoftTeams from '@microsoft/teams-js';
import { setEmployeeGoalsAccessPrivilegesAction } from 'actions/employeeGoalsAccessPrivilegesActions';
import { authenticateAction, updateLoggedInEmployeeAction } from 'actions/loggedInEmployee';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { selectIsAuthenticated, selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { selectMSTeamsContext } from 'selectors/msTeamsSelector';
import { StyledDivider, StyledLink, StyledPaper, StyledTypography } from 'styles/common';

import { MeshLogoWithName } from 'assets/assetsV1/iconComponents';

import { contactEmail, supportEmail } from 'constants/ProjectConstants';
import { getTeamMatch, getProfileMatch, getTeamSettingsMatch, getFeedDetailMatch } from 'constants/helper';
import { routes } from 'constants/routes';

import { msTeamsConnectUser } from 'services/auth';
import { authenticate } from 'services/employee';
import { showErrorSnackbar } from 'services/snackbar';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '100vh',
    width: '100vw',
    justifyContent: 'center',
    '@media (max-width: 800px)': {
      // display: 'block',
      width: '100vw',
      height: '100vh',
      margin: 0,
      overflowX: 'hidden',
    },
  },
  item: {
    display: 'flex',
    justifyContent: 'center',
    padding: '1rem',
  },
  loginBtn: {
    padding: '0.3rem 1rem',
  },
}));

function getBodyForAuthenticationCall({ path }) {
  const teamMatch = getTeamMatch(path);
  const teamSettingsMatch = getTeamSettingsMatch(path);
  const feedDetailMatch = getFeedDetailMatch(path);
  let body = {};
  if (teamMatch || teamSettingsMatch) {
    const teamId = teamMatch ? teamMatch.params.teamId : teamSettingsMatch.params.teamId;
    body = { landingPageType: 'TEAM', landingPageUuid: teamId };
  } else if (feedDetailMatch) {
    const { feedId } = feedDetailMatch.params;
    body = { landingPageType: 'FEED', landingPageUuid: feedId };
  } else {
    const profileMatch = getProfileMatch(path);
    if (profileMatch) {
      const { employeeId } = profileMatch.params;
      body = { landingPageType: 'EMPLOYEE', landingPageUuid: employeeId };
    }
  }
  return body;
}

const redirectRouteAfterLogin = (entityId) => {
  if (entityId === 'goals') {
    return routes.goals.list;
  }
  if (entityId === 'tasks') {
    return routes.initiative.audit;
  }
  if (entityId === 'feedback') {
    return routes.msTeams.Feedback;
  }
  if (entityId === 'oneOnOne') {
    return routes.oneOnOne.audit;
  }
  if (entityId === 'reviews') {
    return routes.performanceReview.audit;
  }
  return routes.goals.list;
};

const MSTeamsLogin = ({ history, setAuthenticated, setLoggedInUser, setEmployeeGoalsAccessPrivileges }) => {
  const classes = useStyles();

  const handleAuthError = (err) => {
    console.error('Error: ', err);
  };

  const msTeamsContext = useSelector(selectMSTeamsContext);

  const { entityId } = msTeamsContext ?? {};

  const { userObjectId } = msTeamsContext ?? {};

  const redirectRoute = redirectRouteAfterLogin(entityId);

  const handleSuccess = () => {
    const body = getBodyForAuthenticationCall({ path: history.location.pathname });
    authenticate(body)
      .then(async (res) => {
        await msTeamsConnectUser(userObjectId);
        setLoggedInUser(res.employee);
        setAuthenticated(true);
        if (history?.location?.state?.referer?.pathname) {
          history.push(history.location.state.referer.pathname);
        } else {
          history.push(redirectRoute);
        }
        /* TODO: add goals access control privileges here as well. */
        setEmployeeGoalsAccessPrivileges(res?.accessPrivileges);
      })
      .catch((err) => {
        if (err.status === 403 || err.status === 500 || err.status === 400) {
          // need to be checked for netchex login if msteams is enabled for netchex
          window.location.href = routes.home;
        } else if (err.status === 401) {
          setAuthenticated(false);
        }
      });
  };

  const meshLogin = () => {
    microsoftTeams.authentication.authenticate({
      url: `${window.location.origin}/login?prompt=msteams`,
      width: 710,
      height: 576,
      successCallback(result) {
        handleSuccess();
      },
      failureCallback(reason) {
        handleAuthError(reason);
      },
    });
  };

  return (
    <Container className={classes.container}>
      <StyledPaper padding="2rem 1rem">
        <Grid container direction="column">
          <Grid className={classes.item} item xs={12}>
            <MeshLogoWithName boxSize="105px" />
          </Grid>
          <Grid className={classes.item} item xs={12}>
            <Button className={classes.loginBtn} variant="contained" color="primary" onClick={meshLogin}>
              Login with Mesh
            </Button>
          </Grid>
          <StyledDivider topGutter={4} />
          <Grid className={classes.item} item xs={12}>
            <StyledTypography variant="body1">
              - Click on login button and enter your <b>Mesh credentials</b>. <br />- For new signups, please contact{' '}
              <StyledLink display="inline-block" href={`mailto:${contactEmail}`}>
                {contactEmail}
              </StyledLink>
              .<br />- In case of any issues/concerns, please reach out at{' '}
              <StyledLink display="inline-block" href={`mailto:${supportEmail}`}>
                {supportEmail}
              </StyledLink>
              .
            </StyledTypography>
          </Grid>
        </Grid>
      </StyledPaper>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: selectIsAuthenticated(state),
  loggedInEmployee: selectLoggedInEmployeeData(state),
});

const mapDispatchToProps = (dispatch) => ({
  setAuthenticated: (isAuthenticated) => {
    dispatch(authenticateAction(isAuthenticated));
  },
  setLoggedInUser: (employee) => {
    dispatch(updateLoggedInEmployeeAction(employee));
  },
  dispatchShowErrorSnackbar: (message) => {
    dispatch(showErrorSnackbar(message));
  },
  setEmployeeGoalsAccessPrivileges: (accessPrivileges) => {
    dispatch(setEmployeeGoalsAccessPrivilegesAction(accessPrivileges));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MSTeamsLogin));
